import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import i18n from '../../lang/i18n/index.js';
import { DATE_PART, FOCUS_ELEMENT } from './constants.js';
import dayjs from '../../utils/dayjs.js';

var dateToObj = function dateToObj(date) {
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate()
  };
};
var shiftDate = function shiftDate(currentDate, offset, type) {
  var isFirstOfMonth = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var year = currentDate.year,
      month = currentDate.month,
      date = currentDate.date;

  switch (type) {
    case DATE_PART.YEAR:
      {
        return new Date(year + offset, month, isFirstOfMonth ? 1 : date);
      }

    case DATE_PART.MONTH:
      {
        return new Date(year, month + offset, isFirstOfMonth ? 1 : date);
      }

    case DATE_PART.DAY:
    default:
      {
        return new Date(year, month, date + offset);
      }
  }
};
var clampMonth = function clampMonth(month, adj) {
  return month + adj >= 0 ? (month + adj) % 12 : (month + adj) % 12 + 12;
};
/**
 * Returns the last day of the month for the given year and month
 * @param {number} year  - year value
 * @param {number} month - month value
 */

var getLastDayOfMonth = function getLastDayOfMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
};
/**
 * Used to check if the day for the given date should be disabled or not
 * @param {Date} date - date to check against
 * @param {Date} before - date before which picker should not be selectable
 * @param {Date} after - date after which picker should not be selectable
 */

var isDisabledDay = function isDisabledDay(date, before, after) {
  if (!date) {
    return false;
  }

  var adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (before) {
    // Need to initialize new Date object to avoid issues with timestamps
    var adjustedBeforeDate = new Date(before.getFullYear(), before.getMonth(), before.getDate());

    if (dayjs(adjustedDate).isBefore(adjustedBeforeDate)) {
      return true;
    }
  }

  if (after) {
    // Need to initialize new Date object to avoid issues with timestamps
    var adjustedAfterDate = new Date(after.getFullYear(), after.getMonth(), after.getDate());

    if (dayjs(adjustedDate).isAfter(adjustedAfterDate)) {
      return true;
    }
  }

  return false;
};
/**
 * Used to determine if month toggles should be disabled or not.
 * If date month is within the {before - after} range, it will not be disabled.
 * @param {Date} date - date to check against
 * @param {Date} before - date before which picker should not be selectable
 * @param {Date} after - date after which picker should not be selectable
 */

var isDisabledMonth = function isDisabledMonth(date, before, after) {
  if (!date) {
    return false;
  }

  var adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (before) {
    // Set to first day of the month for comparison purposes
    var adjustedBeforeDate = new Date(before.getFullYear(), before.getMonth(), 1);

    if (dayjs(adjustedDate).isBefore(adjustedBeforeDate)) {
      return true;
    }
  }

  if (after) {
    // Set to last day of the month for comparison purposes
    var adjustedAfterDate = new Date(after.getFullYear(), after.getMonth(), getLastDayOfMonth(after.getFullYear(), after.getMonth()));

    if (dayjs(adjustedDate).isAfter(adjustedAfterDate)) {
      return true;
    }
  }

  return false;
};
var genDaysOfMonth = function genDaysOfMonth(year, month) {
  var disabledDays = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var date = new Date(year, month, 1);
  var before = disabledDays.before,
      after = disabledDays.after;
  var daysOfMonth = [];
  var dayOfWeek = date.getDay();
  var lastMonth = clampMonth(month, -1);
  var lastMonthYear = month - 1 < 0 ? year - 1 : year;
  var nextMonth = clampMonth(month, +1);
  var nextMonthYear = month + 1 > 11 ? year + 1 : year;
  var daysInMonthPrev = new Date(year, month, 0).getDate();
  var daysInMonth = new Date(year, month + 1, 0).getDate();
  var totalDays = daysInMonth + dayOfWeek >= 35 ? 6 * 7 : 5 * 7; // add days from previous month that are part of the first week

  for (var i = dayOfWeek; i > 0; i -= 1) {
    var day = {
      month: lastMonth,
      date: daysInMonthPrev - i + 1,
      year: lastMonthYear
    };
    var newDate = new Date(day.year, day.month, day.date);
    daysOfMonth.push(_objectSpread(_objectSpread({}, day), {}, {
      isDisabled: isDisabledDay(newDate, before, after)
    }));
  } // add all days in current month


  for (var _i = 1; _i <= daysInMonth; _i += 1) {
    var _day = {
      date: _i,
      month: month,
      year: year
    };

    var _newDate = new Date(_day.year, _day.month, _day.date);

    daysOfMonth.push(_objectSpread(_objectSpread({}, _day), {}, {
      isDisabled: isDisabledDay(_newDate, before, after)
    }));
  } // add days from next month that are part of the last week


  var daysLeft = totalDays - daysInMonth - dayOfWeek;

  for (var _i2 = 1; _i2 <= daysLeft; _i2 += 1) {
    var _day2 = {
      month: nextMonth,
      date: _i2,
      year: nextMonthYear
    };

    var _newDate2 = new Date(_day2.year, _day2.month, _day2.date);

    daysOfMonth.push(_objectSpread(_objectSpread({}, _day2), {}, {
      isDisabled: isDisabledDay(_newDate2, before, after)
    }));
  }

  return daysOfMonth;
};
var getMonths = function getMonths(lang) {
  return [i18n[lang].global.month.january, i18n[lang].global.month.february, i18n[lang].global.month.march, i18n[lang].global.month.april, i18n[lang].global.month.may, i18n[lang].global.month.june, i18n[lang].global.month.july, i18n[lang].global.month.august, i18n[lang].global.month.september, i18n[lang].global.month.october, i18n[lang].global.month.november, i18n[lang].global.month.december];
};
var getWeekdays = function getWeekdays(lang) {
  return [{
    abbr: i18n[lang].global.dayAbbr.sunday,
    title: i18n[lang].global.day.sunday
  }, {
    abbr: i18n[lang].global.dayAbbr.monday,
    title: i18n[lang].global.day.monday
  }, {
    abbr: i18n[lang].global.dayAbbr.tuesday,
    title: i18n[lang].global.day.tuesday
  }, {
    abbr: i18n[lang].global.dayAbbr.wednesday,
    title: i18n[lang].global.day.wednesday
  }, {
    abbr: i18n[lang].global.dayAbbr.thursday,
    title: i18n[lang].global.day.thursday
  }, {
    abbr: i18n[lang].global.dayAbbr.friday,
    title: i18n[lang].global.day.friday
  }, {
    abbr: i18n[lang].global.dayAbbr.saturday,
    title: i18n[lang].global.day.saturday
  }];
};
var getErrors = function getErrors(errors, inputError) {
  var errorList = _toConsumableArray(errors);

  if (inputError) {
    errorList.push({
      isError: true,
      id: 'datepicker-input-error',
      msg: inputError
    });
  }

  return errorList;
};
var getPrevElement = function getPrevElement(currentElement, prevMonthDisabled, nextMonthDisabled) {
  var TOGGLE_PICKER = FOCUS_ELEMENT.TOGGLE_PICKER,
      PREV_MONTH = FOCUS_ELEMENT.PREV_MONTH,
      NEXT_MONTH = FOCUS_ELEMENT.NEXT_MONTH,
      DAY = FOCUS_ELEMENT.DAY;
  var monthTogglesDisabled = prevMonthDisabled && nextMonthDisabled;

  if (currentElement === TOGGLE_PICKER) {
    return DAY;
  }

  if (currentElement === PREV_MONTH) {
    return TOGGLE_PICKER;
  }

  if (currentElement === NEXT_MONTH) {
    if (prevMonthDisabled) {
      return TOGGLE_PICKER;
    }

    return PREV_MONTH;
  }

  if (nextMonthDisabled && !prevMonthDisabled) {
    return PREV_MONTH;
  }

  if (monthTogglesDisabled) {
    return TOGGLE_PICKER;
  }

  return NEXT_MONTH;
};
var getNextElement = function getNextElement(currentElement, prevMonthDisabled, nextMonthDisabled) {
  var TOGGLE_PICKER = FOCUS_ELEMENT.TOGGLE_PICKER,
      PREV_MONTH = FOCUS_ELEMENT.PREV_MONTH,
      NEXT_MONTH = FOCUS_ELEMENT.NEXT_MONTH,
      DAY = FOCUS_ELEMENT.DAY;
  var monthTogglesDisabled = prevMonthDisabled && nextMonthDisabled;

  if (currentElement === TOGGLE_PICKER) {
    if (prevMonthDisabled && !nextMonthDisabled) {
      return NEXT_MONTH;
    }

    if (monthTogglesDisabled) {
      return DAY;
    }

    return PREV_MONTH;
  }

  if (currentElement === PREV_MONTH) {
    if (nextMonthDisabled) {
      return DAY;
    }

    return NEXT_MONTH;
  }

  if (currentElement === NEXT_MONTH) {
    return DAY;
  }

  return TOGGLE_PICKER;
};

export { clampMonth, dateToObj, genDaysOfMonth, getErrors, getLastDayOfMonth, getMonths, getNextElement, getPrevElement, getWeekdays, isDisabledDay, isDisabledMonth, shiftDate };
